export type FieldErrors = Record<string, string>;

const labels: Record<string, string> = {
  name: 'Name',
  email: 'Email address',
  contactEmail: 'Email address',
  contactName: 'Contact name',
  contactPhone: 'Phone number',
  startsAt: 'Start time',
  endsAt: 'End time',
  displayAfter: 'Display date',
  hideAfter: 'Hide date',
  address: 'Location',
  latitude: 'Location',
  longitude: 'Location',
  categoryId: 'Category',
  ticketPriceMinCents: 'Minimum ticket price',
  ticketPriceMaxCents: 'Maximum ticket price',
  primaryColor: 'Primary color',
  secondaryColor: 'Secondary color',
  accentColor: 'Accent color',
  complimentaryColor: 'Complimentary color',
  thumbnailImage: 'Thumbnail image',
  mapImage: 'Map image',
  images: 'Images',
  logoFiles: 'Sponsor images',
  aboutPdf: 'PDF',
  title: 'Title',
  body: 'Message',
  description: 'Description',
  textLocation: 'Simple location',
  eventId: 'Event',
  topic: 'Audience',
};

export function getApiProblem(error: unknown): { message: string; fields: FieldErrors } {
  const failure = error as {
    response?: { status?: number; data?: { details?: Record<string, { message?: string }> } };
    code?: string;
    isAxiosError?: boolean;
  } | null;
  const status = failure?.response?.status;
  const fields: FieldErrors = {};
  if (status === 422 || status === 400) {
    for (const [path, detail] of Object.entries(failure?.response?.data?.details || {})) {
      const lastPart =
        path
          .split('.')
          .filter((part) => !part.startsWith('$'))
          .pop() || '';
      const field = lastPart === 'latitude' || lastPart === 'longitude' ? 'address' : lastPart;
      if (!labels[field]) continue;
      fields[field] = /required/i.test(detail?.message || '')
        ? `${labels[field]} is required.`
        : `Please check ${labels[field].toLowerCase()}.`;
    }
    return {
      fields,
      message: Object.keys(fields).length
        ? 'Some information could not be accepted. Please check the highlighted fields. If everything looks correct, contact support.'
        : 'Some information could not be accepted. Please review the form. If everything looks correct, contact support.',
    };
  }
  const messages: Record<number, string> = {
    401: 'Your sign-in has expired. Please sign in again, then retry this action.',
    403: 'Your account does not have permission to do this. Please contact an administrator.',
    404: 'This item could not be found. It may have been removed. Refresh the page and try again.',
    409: 'This information conflicts with an existing item. Please refresh the page and review your changes.',
    413: 'The selected files are too large. Please choose smaller files and try again.',
    415: 'This file type is not supported. Please choose a supported image or PDF.',
    429: 'Too many requests were made at once. Please wait a moment and try again.',
  };
  if (status && messages[status]) return { message: messages[status], fields };
  if (status && status >= 500)
    return {
      fields,
      message:
        'The service is having trouble completing this request. Please refresh the list to check whether your changes were saved before trying again. If it keeps happening, contact support.',
    };
  if (!status && (failure?.isAxiosError || failure?.code === 'ECONNABORTED' || failure?.code === 'ETIMEDOUT')) {
    return {
      fields,
      message:
        'We could not confirm whether the request completed. Check your internet connection and refresh the list before trying again, to avoid creating a duplicate.',
    };
  }
  return {
    fields,
    message:
      'Something went wrong while completing this action. Please try again. If it keeps happening, contact support.',
  };
}

export function notifyProblem(message: string | null) {
  window.dispatchEvent(new CustomEvent('gretna-api-problem', { detail: message }));
}

export const required = (value: unknown, label: string) =>
  value === null || value === undefined || (typeof value === 'string' && !value.trim()) ? `${label} is required.` : '';

export const emailError = (value: string) =>
  required(value, 'Email address') ||
  (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ? '' : 'Enter a valid email address.');

export function priceCents(value: string) {
  return Math.round(Number(value.replace(/[$,\s]/g, '')) * 100);
}

export function priceError(value: string) {
  const cleaned = value.replace(/[$,\s]/g, '');
  return !cleaned || (/^\d+(\.\d{1,2})?$/.test(cleaned) && priceCents(value) <= 2147483647)
    ? ''
    : 'Enter a valid price of zero or more, with no more than two decimal places.';
}

export function dateErrors(
  start: { isValid(): boolean; valueOf(): number } | null,
  end: { isValid(): boolean; valueOf(): number } | null,
): FieldErrors {
  const errors: FieldErrors = {};
  if (!start?.isValid()) errors.startsAt = 'Enter a valid start date and time.';
  if (!end?.isValid()) errors.endsAt = 'Enter a valid end date and time.';
  if (start?.isValid() && end?.isValid() && end.valueOf() < start.valueOf())
    errors.endsAt = 'End time must be at or after start time.';
  return errors;
}

export function partialSaveMessage(item: string, error: unknown) {
  return `The ${item} was saved, but we could not confirm that all images uploaded. ${
    getApiProblem(error).message
  } Your remaining files are still selected; retrying updates this ${item}.`;
}
